import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { adminApi } from '../../../api/adminApi';
import { DatePicker } from '@shared-ui/components';
import { QUESTION_SUBJECT_OPTIONS, SUBJECT_NAMES, titleCaseSubjectKey } from '@padcanvas/shared';
import type { UserSubscription } from '../../../types/settings';
import { BillingPanel } from './BillingPanel';

interface SubscriptionsTabProps {
  userId: string;
  user?: { name: string; bypassSubscription?: boolean };
}

export function SubscriptionsTab({ userId, user }: SubscriptionsTabProps) {
  const queryClient = useQueryClient();

  const { data: userSubs, isLoading: subsLoading } = useQuery({
    queryKey: ['userSubscriptions', userId],
    queryFn: () => adminApi.getUserSubscriptions(userId),
    enabled: !!userId,
  });

  const { data: subscriptionDefs } = useQuery({
    queryKey: ['subscriptionDefinitions'],
    queryFn: adminApi.getSubscriptionDefinitions,
  });

  const [showAssignForm, setShowAssignForm] = useState(false);
  const [assignSubId, setAssignSubId] = useState('');
  const [assignStartDate, setAssignStartDate] = useState('');
  const [assignEndDate, setAssignEndDate] = useState('');
  const [assignSubjects, setAssignSubjects] = useState<string[]>([]);

  const assignMutation = useMutation({
    mutationFn: () =>
      adminApi.assignUserSubscription(userId, {
        subscriptionId: assignSubId,
        startDate: assignStartDate,
        endDate: assignEndDate,
        subjects: assignSubjects,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['userSubscriptions', userId] });
      queryClient.invalidateQueries({ queryKey: ['userEligibility', userId] });
      queryClient.invalidateQueries({ queryKey: ['userBilling', userId] });
      setShowAssignForm(false);
      setAssignSubId('');
      setAssignStartDate('');
      setAssignEndDate('');
      setAssignSubjects([]);
    },
  });

  const cancelSubMutation = useMutation({
    mutationFn: (subId: string) => adminApi.cancelUserSubscription(userId, subId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['userSubscriptions', userId] });
      queryClient.invalidateQueries({ queryKey: ['userBilling', userId] });
      queryClient.invalidateQueries({ queryKey: ['userEligibility', userId] });
    },
  });

  const bypassMutation = useMutation({
    mutationFn: (bypass: boolean) => adminApi.updateUser(userId, { bypassSubscription: bypass }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      queryClient.invalidateQueries({ queryKey: ['user-detail', userId] });
      queryClient.invalidateQueries({ queryKey: ['userEligibility', userId] });
      queryClient.invalidateQueries({ queryKey: ['userBilling', userId] });
    },
  });

  return (
    <div className="features-section">
      {subsLoading ? (
        <div className="loading">Loading subscriptions...</div>
      ) : !userSubs || userSubs.length === 0 ? (
        <p className="no-features-message">No subscriptions assigned to this user.</p>
      ) : (
        <table className="dt dt--compact" style={{ marginBottom: 16 }}>
          <thead>
            <tr>
              <th style={{ padding: '8px 10px' }}>Subscription</th>
              <th style={{ padding: '8px 10px' }}>Subjects</th>
              <th style={{ padding: '8px 10px' }}>Start</th>
              <th style={{ padding: '8px 10px' }}>End</th>
              <th style={{ padding: '8px 10px' }}>Source</th>
              <th style={{ padding: '8px 10px' }}>Status</th>
              <th style={{ padding: '8px 10px' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {userSubs.map((sub: UserSubscription) => {
              const isExpired = new Date(sub.endDate) < new Date();
              const isActive = sub.status === 'active' && !isExpired;
              return (
                <tr key={sub.id}>
                  <td style={{ padding: '8px 10px' }}>
                    <strong>{sub.subscriptionName}</strong>
                    <br />
                    <span style={{ fontSize: 11, color: 'var(--text-tertiary)', fontFamily: 'monospace' }}>{sub.subscriptionId}</span>
                  </td>
                  <td style={{ padding: '8px 10px' }}>{sub.subjects?.length ? sub.subjects.map(s => SUBJECT_NAMES[s] || titleCaseSubjectKey(s)).join(', ') : 'All'}</td>
                  <td style={{ padding: '8px 10px' }}>{new Date(sub.startDate).toLocaleDateString()}</td>
                  <td style={{ padding: '8px 10px' }}>{new Date(sub.endDate).toLocaleDateString()}</td>
                  <td style={{ padding: '8px 10px' }}>
                    {sub.source ?? <span style={{ color: 'var(--text-tertiary)' }}>—</span>}
                    {sub.orderId && (
                      <>
                        <br />
                        <Link
                          to={`/billing/orders?order=${encodeURIComponent(sub.orderId)}`}
                          style={{ fontSize: 11, fontFamily: 'monospace' }}
                          title="Order that granted this"
                        >
                          {sub.orderId}
                        </Link>
                      </>
                    )}
                    {sub.purchasedBy && (
                      <>
                        <br />
                        <span style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>
                          by{' '}
                          {sub.source === 'teacher'
                            ? <Link to={`/users/${encodeURIComponent(sub.purchasedBy)}`} style={{ fontFamily: 'monospace' }}>{sub.purchasedBy}</Link>
                            : <span style={{ fontFamily: 'monospace' }}>{sub.purchasedBy}</span>}
                        </span>
                      </>
                    )}
                  </td>
                  <td style={{ padding: '8px 10px' }}>
                    <span style={{
                      padding: '2px 8px', borderRadius: 10, fontSize: 11, fontWeight: 600,
                      background: sub.status === 'cancelled' ? 'var(--bg-tertiary)' : isExpired ? 'var(--status-warning-bg)' : 'var(--status-success-bg)',
                      color: sub.status === 'cancelled' ? 'var(--text-tertiary)' : isExpired ? 'var(--status-warning-text)' : 'var(--status-success-text)',
                    }}>
                      {sub.status === 'cancelled' ? 'Cancelled' : isExpired ? 'Expired' : 'Active'}
                    </span>
                  </td>
                  <td style={{ padding: '8px 10px' }}>
                    {isActive && (
                      <button
                        className="reset-btn"
                        onClick={() => {
                          if (window.confirm(`Cancel subscription "${sub.subscriptionName}"?`)) {
                            cancelSubMutation.mutate(sub.id);
                          }
                        }}
                        disabled={cancelSubMutation.isPending}
                        style={{ color: 'var(--color-error)', borderColor: 'var(--color-error)' }}
                      >
                        Cancel
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}

      {showAssignForm ? (
        <div style={{ border: '1px solid var(--border-color)', borderRadius: 8, padding: 16, background: 'var(--bg-secondary)' }}>
          <h4 style={{ margin: '0 0 12px 0', fontSize: 14 }}>Assign Subscription</h4>
          <div style={{ display: 'flex', gap: 12, marginBottom: 12, flexWrap: 'wrap' }}>
            <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 13, fontWeight: 500, color: 'var(--text-secondary)', flex: 1, minWidth: 150 }}>
              Subscription
              <select value={assignSubId} onChange={(e) => setAssignSubId(e.target.value)} style={{ padding: '6px 10px', border: '1px solid var(--border-color-dark)', borderRadius: 6, fontSize: 13 }}>
                <option value="">Select...</option>
                {(subscriptionDefs ?? []).map(s => (
                  <option key={s.id} value={s.id}>{s.name}</option>
                ))}
              </select>
            </label>
            <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 13, fontWeight: 500, color: 'var(--text-secondary)', flex: 1, minWidth: 150 }}>
              Start Date
              <DatePicker value={assignStartDate} onChange={setAssignStartDate} ariaLabel="Start date" />
            </label>
            <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 13, fontWeight: 500, color: 'var(--text-secondary)', flex: 1, minWidth: 150 }}>
              End Date
              <DatePicker value={assignEndDate} onChange={setAssignEndDate} ariaLabel="End date" />
            </label>
          </div>
          <div style={{ marginBottom: 12 }}>
            <label style={{ fontSize: 13, fontWeight: 500, color: 'var(--text-secondary)', display: 'block', marginBottom: 6 }}>
              Subjects <span style={{ fontWeight: 400, color: 'var(--text-tertiary)' }}>(leave empty for all)</span>
            </label>
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
              {QUESTION_SUBJECT_OPTIONS.map(s => (
                <label key={s} style={{ fontSize: 13, display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={assignSubjects.includes(s)}
                    onChange={() => setAssignSubjects(prev =>
                      prev.includes(s) ? prev.filter(x => x !== s) : [...prev, s]
                    )}
                  />
                  {SUBJECT_NAMES[s] || titleCaseSubjectKey(s)}
                </label>
              ))}
            </div>
          </div>
          <div className="features-actions">
            <button
              className="save-features-btn"
              onClick={() => assignMutation.mutate()}
              disabled={!assignSubId || !assignStartDate || !assignEndDate || assignMutation.isPending}
            >
              {assignMutation.isPending ? 'Assigning...' : 'Assign'}
            </button>
            <button className="reset-btn" onClick={() => setShowAssignForm(false)}>Cancel</button>
            {assignMutation.isError && <span className="status-error">{(assignMutation.error as Error).message}</span>}
          </div>
        </div>
      ) : (
        <button
          onClick={() => setShowAssignForm(true)}
          style={{
            padding: '8px 16px', border: '1px dashed var(--border-color-dark)', borderRadius: 8,
            background: 'transparent', color: 'var(--text-secondary)', fontSize: 13, cursor: 'pointer',
          }}
        >
          + Assign Subscription
        </button>
      )}

      <BillingPanel userId={userId} />

      {/* Bypass Subscription toggle */}
      {user && (
        <div className="bypass-section">
          <div className="bypass-section-header">
            <div>
              <span className="bypass-section-title">Bypass Subscription</span>
              <span className="bypass-section-desc">Skip all eligibility/quota checks. Rate limits are still enforced.</span>
            </div>
            <div className="bypass-section-actions">
              {user.bypassSubscription && <span className="bypass-badge">Active</span>}
              <button
                className={user.bypassSubscription ? 'bypass-disable-btn' : 'bypass-enable-btn'}
                onClick={() => {
                  const next = !user.bypassSubscription;
                  if (window.confirm(`${next ? 'Enable' : 'Disable'} subscription bypass for "${user.name}"?`)) {
                    bypassMutation.mutate(next);
                  }
                }}
                disabled={bypassMutation.isPending}
              >
                {bypassMutation.isPending
                  ? 'Updating...'
                  : user.bypassSubscription ? 'Disable Bypass' : 'Enable Bypass'}
              </button>
              {bypassMutation.isError && (
                <span className="status-error">
                  Failed: {(bypassMutation.error as Error).message}
                </span>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
